import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, KeyRound, ArrowRight, AlertCircle, CheckCircle2 } from 'lucide-react';
import { authService } from '../services/authService';

/**
 * Recupero password: chi l'ha dimenticata riceve per email un link a
 * `/reimposta-password` (backend `auth/recupero_router.py`). La risposta è
 * sempre la stessa, che l'indirizzo sia registrato o no.
 */
export default function PasswordDimenticataPage() {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setError(null);
    setIsLoading(true);
    try {
      const { data } = await authService.forgotPassword(email.trim());
      setMessage(data.message);
    } catch (err: any) {
      if (err?.response?.status === 429) {
        setError('Troppe richieste: riprova più tardi.');
      } else {
        setError('Errore di connessione. Riprova tra qualche istante.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div data-testid="forgot-password-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12">
        <div className="max-w-lg mx-auto space-y-4">
          <div className="rounded-xl border bg-white p-6 sm:p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl bg-[hsl(210,40%,93%)] flex items-center justify-center">
                <KeyRound className="w-6 h-6 text-[hsl(210,64%,29%)]" />
              </div>
              <div>
                <h1 className="text-2xl font-semibold">Password dimenticata</h1>
                <p className="text-sm text-muted-foreground">Ti mandiamo un link per sceglierne una nuova</p>
              </div>
            </div>

            {error && (
              <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 border border-red-200 mb-4 text-sm text-red-700">
                <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                {error}
              </div>
            )}

            {message ? (
              <div className="flex items-start gap-2 p-3 rounded-lg bg-green-50 border border-green-200 text-sm text-green-800" data-testid="forgot-password-sent">
                <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <div>
                  {message}
                  <p className="mt-1 text-green-700">Il link vale 60 minuti. Controlla anche la cartella dello spam.</p>
                </div>
              </div>
            ) : (
              <form className="space-y-4" onSubmit={handleSubmit} data-testid="forgot-password-form">
                <div>
                  <label className="text-xs font-medium mb-1.5 block">Email</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <input
                      type="email"
                      className="w-full h-10 pl-9 pr-3 rounded-lg border text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[hsl(var(--dtk-focus))]"
                      placeholder="L'email con cui accedi"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      autoComplete="username"
                      data-testid="forgot-password-email"
                      required
                    />
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={isLoading}
                  data-testid="forgot-password-submit"
                  className="w-full h-10 rounded-lg bg-[hsl(210,64%,29%)] text-white text-sm font-medium flex items-center justify-center gap-2 hover:bg-[hsl(210,64%,24%)] transition disabled:opacity-60"
                >
                  {isLoading ? (
                    <div className="w-4 h-4 rounded-full border-2 border-white border-t-transparent animate-spin" />
                  ) : (
                    <>Invia il link <ArrowRight className="w-4 h-4" /></>
                  )}
                </button>
              </form>
            )}

            <div className="mt-6 pt-4 border-t text-sm text-center text-muted-foreground">
              Ti sei ricordato la password?{' '}
              <Link to="/accedi" className="font-medium text-[hsl(210,64%,29%)] hover:underline">
                Accedi
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
